import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageLayout from '@/components/layout/PageLayout';
import { TabList } from '@/components/ui/navigation/TabList';
import { FormLayout } from '@/components/layout';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { useAttendanceConfiguration } from '@/hooks/attendance';
import {
  AttendanceConfigurationGeneralForm,
  AttendanceConfigurationCalculationForm,
  AttendanceConfigurationNotificationsForm,
  AttendanceConfigurationValidationsForm,
  AttendanceConfigurationWorkdayTab,
} from '@/components/features/attendance/configuration';


export const AttendanceConfiguration: React.FC = () => {
  const { t: translate } = useTranslation('attendance');
  const [activeTab, setActiveTab] = useState('general');
  const {
    form,
    isLoading,
    isSaving,
    isDirty,
    handleSave,
    handleCancel,
    markingEventOptions,
    companyControlOptions,
    recipientOptions,
    roleOptions,
    eventDefinitionMethodOptions,
    halfDayAbsenteeismOptions,
    automaticModeOptions,
  } = useAttendanceConfiguration({ onValidationError: setActiveTab });

  const tabs = [
    {
      id: 'general',
      label: translate('configuration.tabs.general'),
      content: (
        <AttendanceConfigurationGeneralForm
          form={form}
          markingEventOptions={markingEventOptions}
          eventDefinitionMethodOptions={eventDefinitionMethodOptions}
        />
      ),
    },
    {
      id: 'calculation',
      label: translate('configuration.tabs.calculation'),
      content: (
        <AttendanceConfigurationCalculationForm
          form={form}
          halfDayAbsenteeismOptions={halfDayAbsenteeismOptions}
        />
      ),
    },
    {
      id: 'notifications',
      label: translate('configuration.tabs.notifications'),
      content: (
        <AttendanceConfigurationNotificationsForm
          form={form}
          companyControlOptions={companyControlOptions}
          recipientOptions={recipientOptions}
        />
      ),
    },
    {
      id: 'validations',
      label: translate('configuration.tabs.validations'),
      content: (
        <AttendanceConfigurationValidationsForm
          form={form}
          roleOptions={roleOptions}
          automaticModeOptions={automaticModeOptions}
        />
      ),
    },
    {
      id: 'workday',
      label: translate('configuration.tabs.workday'),
      content: <AttendanceConfigurationWorkdayTab />,
    },
  ];

  return (
    <PageLayout isLoading={isLoading}>
      <div className="flex flex-col gap-6">
        <TabList tabs={tabs} defaultActiveTab={activeTab} onTabChange={setActiveTab} />

        {/* Footer con botones – visible solo en tabs de formulario */}
        {activeTab !== 'workday' && (
          <FormLayout.Footer
            cancelButton={
              <PrimaryButton variant="outline" onClick={handleCancel} disabled={!isDirty}>
                {translate('configuration.actions.cancel')}
              </PrimaryButton>
            }
            primaryButton={
              <PrimaryButton
                onClick={handleSave}
                isLoading={isSaving}
                disabled={!isDirty || isSaving}
              >
                {translate('configuration.actions.saveChanges')}
              </PrimaryButton>
            }
          />
        )}
      </div>
    </PageLayout>
  );
};

export default AttendanceConfiguration;
